import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import { PoSourceCell, PoTypeTag } from '../../components/orderType';
import { HeaderActions } from '../../components/pickers';
import PurchaseOrderFormModal from '../../components/PurchaseOrderFormModal';
import {
  codeCls,
  dateCls,
  LinesQty,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  PageHeader,
} from '../../components/ui';
import { usePurchaseOrderActions } from '../../components/usePurchaseOrderActions';
import { fmtDateTime, fmtQty } from '../../format';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { PO_FILTERS } from '../../filterFields';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { PO_STATUS, PO_TABS } from '../../status';
import type {
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderStatus,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

type Tab = (typeof PO_TABS)[number];

const PurchaseOrderListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  const q = useListQuery<Tab>(PO_TABS, 'ALL', PO_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;
  const [creating, setCreating] = useState(false);
  /** The DRAFT being edited, with its lines (a row carries none). */
  const [editing, setEditing] = useState<PurchaseOrderDetail | null>(null);

  const list = useLoad(
    (signal) =>
      warehouseApi.listPurchaseOrders(
        {
          hubID,
          status: q.tab === 'ALL' ? null : (q.tab as PurchaseOrderStatus),
          ...filterApi,
          search,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, q.tab, filterKey, search, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) =>
      warehouseApi.getPurchaseOrderSummary(
        { hubID, ...filterApi, search },
        { signal }
      ),
    [hubID, filterKey, search, refreshToken],
    ready
  );
  // After a mutation: reload what this page shows.
  const reloadList = list.reload;
  const reloadSummary = summary.reload;
  const onChanged = useCallback(() => {
    reloadList();
    reloadSummary();
  }, [reloadList, reloadSummary]);
  const actions = usePurchaseOrderActions(onChanged);
  const openEdit = useCallback(async (po: PurchaseOrder) => {
    try {
      setEditing(await warehouseApi.getPurchaseOrder(po.id));
    } catch (err) {
      toast.error('Gagal membuka PO', {
        description: parseWarehouseError(err).message,
      });
    }
  }, []);
  const { requestConfirm, requestCancel, canConfirm, canCancel, busyID } =
    actions;

  const tabs = PO_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : PO_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (summary.data.byStatus.find((b) => b.status === k)?.count ?? 0)
      : null,
  }));

  const columns = useMemo<TableDataColumn<PurchaseOrder>[]>(
    () => [
      {
        key: 'code',
        title: 'PO number',
        render: (_v, x) => <span className={codeCls}>{x.code}</span>,
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, x) => (
          <span className={nameCls}>
            {x.client?.name ?? `Client #${x.clientID}`}
          </span>
        ),
      },
      {
        key: 'type',
        title: 'Tipe',
        render: (_v, x) => <PoTypeTag type={x.type} />,
      },
      {
        key: 'source',
        title: 'Sumber',
        render: (_v, x) => <PoSourceCell po={x} />,
      },
      {
        key: 'hub',
        title: 'Hub tujuan',
        render: (_v, x) => (
          <span className={nameCls}>{x.hub?.name ?? `Hub #${x.hubID}`}</span>
        ),
      },
      {
        key: 'eta',
        title: 'Est. arrival',
        render: (_v, x) => (
          <span className={dateCls}>{fmtDateTime(x.estArrivalAt)}</span>
        ),
      },
      {
        key: 'lines',
        title: 'Lines / qty',
        align: 'right',
        render: (_v, x) => (
          <LinesQty lines={x.lineCount} qty={fmtQty(x.totalQty)} />
        ),
      },
      {
        key: 'poStatus',
        title: 'Status',
        render: (_v, x) => <MappedBadge map={PO_STATUS} value={x.status} />,
      },
      {
        key: 'createdBy',
        title: 'Created by',
        render: (_v, x) => <span className={metaCls}>{x.createdBy}</span>,
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, x) => (
          <ActionDropdown
            busy={busyID === x.id}
            actions={[
              {
                key: 'view',
                label: 'View details',
                icon: 'ri-eye-line',
                onClick: () =>
                  navigate(`${WAREHOUSE_PATHS.purchaseOrder}/${x.id}`),
              },
              {
                key: 'edit',
                label: 'Ubah',
                icon: 'ri-edit-line',
                hidden: x.status !== 'DRAFT',
                onClick: () => openEdit(x),
              },
              {
                key: 'confirm',
                label: 'Confirm PO',
                icon: 'ri-check-line',
                hidden: !canConfirm(x),
                disabled: busyID !== null,
                onClick: () => requestConfirm(x),
              },
              {
                key: 'gr',
                label: 'Buat goods receipt',
                icon: 'ri-inbox-archive-line',
                hidden:
                  x.status !== 'CONFIRMED' || x.receiptStatus === 'FINISHED',
                onClick: () =>
                  navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new?po=${x.id}`),
              },
              {
                key: 'cancel',
                label: 'Cancel PO',
                icon: 'ri-close-circle-line',
                destructive: true,
                hidden: !canCancel(x),
                onClick: () => requestCancel(x),
              },
            ]}
          />
        ),
      },
    ],
    [
      navigate,
      busyID,
      canConfirm,
      canCancel,
      requestConfirm,
      requestCancel,
      openEdit,
    ]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Supply management"
        title="Purchase order"
        actions={
          <HeaderActions>
            <Button
              onClick={() => setCreating(true)}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Create PO
            </Button>
          </HeaderActions>
        }
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Search PO, vendor, client…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter purchase order"
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={list.data?.data ?? []}
            columns={columns}
            onRowClick={(x) =>
              navigate(`${WAREHOUSE_PATHS.purchaseOrder}/${x.id}`)
            }
            emptyState={<EmptyTable title="Belum ada purchase order" />}
          />
        )}
      </ListShell>
      {actions.dialogs}
      <PurchaseOrderFormModal
        open={creating}
        initialHubID={hubID}
        onClose={() => setCreating(false)}
        onCreated={(po) => {
          setCreating(false);
          toast.success('PO draft dibuat', { description: po.code });
          navigate(`${WAREHOUSE_PATHS.purchaseOrder}/${po.id}`);
        }}
      />
      {editing && (
        <PurchaseOrderFormModal
          open
          editing={editing}
          initialHubID={editing.order.hubID}
          onClose={() => setEditing(null)}
          onUpdated={() => {
            setEditing(null);
            onChanged();
          }}
          onStale={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default PurchaseOrderListPage;
